import { BaseFeature } from '../../core/base-feature.js';

/**
 * ChallengesFeature - Lockout challenges with puzzles (Frontend)
 * Handles: starting challenges, monitoring progress, listing puzzles
 */
export class ChallengesFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // State
        this.puzzles = [];
        this.currentChallenge = null;
        this.isLoading = false;

        // Event listeners for React components
        this.progressCallbacks = new Set();
        this.updateCallbacks = new Set();
        this.screenshotCallbacks = new Set();

        // Pending promises for request/response
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'challenges',
            name: 'challenges',
            version: 'v1.0',
            capabilities: [
                'start_challenge',
                'cancel_challenge',
                'list_puzzles',
                'get_state'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[Challenges] Initializing...');
        console.log('[Challenges] Initialized');
    }

    async handleMessage(message) {
        const { action, payload, messageId, fromId } = message;

        switch (action) {
            case 'start_response':
                return this.handleStartResponse(payload);

            case 'cancel_response':
                return this.handleCancelResponse(payload);

            case 'list_response':
                return this.handleListResponse(payload);

            case 'get_state_response':
                return this.handleGetStateResponse(payload);

            case 'sabotage_response':
                return this.handleSabotageResponse(payload);

            case 'progress':
                return this.handleProgress(payload);

            case 'initial_state':
                return this.handleInitialState(payload);

            case 'screenshot':
                return this.handleScreenshot(payload);

            default: {
                // Handle generic _response actions
                const baseAction = action.replace(/_response$/, '');
                const resolve = this._pendingRequests.get(baseAction);
                if (resolve) {
                    this._pendingRequests.delete(baseAction);
                    resolve(payload);
                    return;
                }
                console.warn(`[Challenges] Unknown action: ${action}`);
            }
        }
    }

    // =====================================
    // Challenge Management
    // =====================================

    /**
     * Start a challenge
     * @param {string} puzzleType - Puzzle type ID
     * @param {Object} config - Optional puzzle configuration
     * @returns {Promise<Object>} Result
     */
    async startChallenge(puzzleType, config = {}) {
        console.log(`[Challenges] Starting challenge: ${puzzleType}`);

        return new Promise((resolve) => {
            this.sendMessage('start', { puzzleType, config });
            this._pendingRequests.set('start', resolve);
        });
    }

    handleStartResponse(payload) {
        const resolve = this._pendingRequests.get('start');
        if (resolve) {
            this._pendingRequests.delete('start');
            resolve(payload);
        }

        if (payload.success) {
            console.log(`[Challenges] Challenge started: ${payload.puzzleType}`);
            this.currentChallenge = {
                puzzleType: payload.puzzleType,
                metadata: payload.metadata,
                status: 'in_progress',
                progress: 0,
                timeElapsed: 0
            };
            this.notifyUpdateListeners();
        } else {
            console.error('[Challenges] Failed to start challenge:', payload.error);
        }
    }

    /**
     * Cancel the current challenge
     * @returns {Promise<Object>} Result
     */
    async cancelChallenge() {
        console.log('[Challenges] Cancelling challenge');

        return new Promise((resolve) => {
            this.sendMessage('cancel', {});
            this._pendingRequests.set('cancel', resolve);
        });
    }

    handleCancelResponse(payload) {
        const resolve = this._pendingRequests.get('cancel');
        if (resolve) {
            this._pendingRequests.delete('cancel');
            resolve(payload);
        }

        if (payload.success) {
            console.log('[Challenges] Challenge cancelled');
            this.currentChallenge = null;
            this.notifyUpdateListeners();
        } else {
            console.error('[Challenges] Failed to cancel challenge:', payload.error);
        }
    }

    /**
     * List available puzzles
     * @returns {Promise<Array>} Puzzle list
     */
    async listPuzzles() {
        console.log('[Challenges] Requesting puzzle list');
        this.isLoading = true;
        this.notifyUpdateListeners();

        return new Promise((resolve) => {
            this.sendMessage('list', {});
            this._pendingRequests.set('list', resolve);
        });
    }

    handleListResponse(payload) {
        this.isLoading = false;

        const resolve = this._pendingRequests.get('list');
        if (resolve) {
            this._pendingRequests.delete('list');
            resolve(payload);
        }

        if (payload.success) {
            this.puzzles = payload.puzzles || [];
            console.log(`[Challenges] Loaded ${this.puzzles.length} puzzles`);
        } else {
            console.error('[Challenges] Failed to list puzzles:', payload.error);
        }

        this.notifyUpdateListeners();
    }

    /**
     * Get current challenge state
     * @returns {Promise<Object>} Current state
     */
    async getState() {
        console.log('[Challenges] Requesting current state');

        return new Promise((resolve) => {
            this.sendMessage('get_state', {});
            this._pendingRequests.set('get_state', resolve);
        });
    }

    /**
     * Send a sabotage action to mess with the active challenge
     * @param {string} action - Sabotage action key (e.g., 'force_lie', 'trigger_reset')
     * @returns {Promise<Object>} Result
     */
    async sendSabotage(action, params = {}) {
        console.log(`[Challenges] Sending sabotage: ${action}`);

        if (!this.currentChallenge) {
            console.warn('[Challenges] No active challenge to sabotage');
            return { success: false, error: 'No active challenge' };
        }

        return new Promise((resolve) => {
            this.sendMessage('sabotage', { action, params });
            this._pendingRequests.set('sabotage', resolve);
        });
    }

    // =====================================
    // Stats & History
    // =====================================

    async getStats(puzzleType = null) {
        return this._request('get_stats', { puzzleType });
    }

    async getHistory(options = {}) {
        return this._request('get_history', options);
    }

    async getFailureDistribution(puzzleType) {
        return this._request('get_failure_distribution', { puzzleType });
    }

    // =====================================
    // Favorites
    // =====================================

    async saveFavorite(data) {
        return this._request('save_favorite', data);
    }

    async getFavorites() {
        return this._request('get_favorites', {});
    }

    async deleteFavorite(id) {
        return this._request('delete_favorite', { id });
    }

    async launchFavorite(favorite) {
        return this.startChallenge(favorite.puzzle_type || favorite.puzzleType, favorite.config);
    }

    // =====================================
    // Bounties
    // =====================================

    async createBounty(data) {
        return this._request('create_bounty', data);
    }

    async getBounties(status = 'active') {
        return this._request('get_bounties', { status });
    }

    async cancelBounty(id) {
        return this._request('cancel_bounty', { id });
    }

    async forceBounty(bounty) {
        return this.startChallenge(bounty.puzzle_type || bounty.puzzleType, {
            ...bounty.config,
            bountyId: bounty.id
        });
    }

    // =====================================
    // Taunts
    // =====================================

    async sendTaunt(text, durationMs = 4000) {
        return this._request('send_taunt', { text, durationMs });
    }

    async setAiTauntsEnabled(enabled) {
        return this._request('set_ai_taunts', { enabled });
    }

    // =====================================
    // Economy
    // =====================================

    async getBalance() {
        return this._request('get_balance', {});
    }

    // =====================================
    // AI Smart Start
    // =====================================

    async smartStart() {
        return this._request('smart_start', {});
    }

    // =====================================
    // Generic request helper
    // =====================================

    _request(action, payload) {
        return new Promise((resolve) => {
            this.sendMessage(action, payload);
            this._pendingRequests.set(action, resolve);
        });
    }

    handleSabotageResponse(payload) {
        const resolve = this._pendingRequests.get('sabotage');
        if (resolve) {
            this._pendingRequests.delete('sabotage');
            resolve(payload);
        }

        if (payload.success) {
            console.log(`[Challenges] Sabotage applied: ${payload.action}`);
        } else {
            console.warn('[Challenges] Sabotage failed:', payload.error);
        }
    }

    handleGetStateResponse(payload) {
        const resolve = this._pendingRequests.get('get_state');
        if (resolve) {
            this._pendingRequests.delete('get_state');
            resolve(payload);
        }

        if (payload.success && payload.state) {
            this.currentChallenge = payload.state;
            this.notifyUpdateListeners();
        }
    }

    // =====================================
    // Progress Updates
    // =====================================

    handleProgress(payload) {
        this.currentChallenge = payload;

        // Notify progress listeners (real-time updates)
        this.notifyProgressListeners(payload);

        // Check for completion
        if (payload.status === 'completed' || payload.status === 'cancelled') {
            this.currentChallenge = null;
            this.notifyUpdateListeners();
        }
    }

    handleInitialState(payload) {
        console.log('[Challenges] Received initial state from desktop');

        const { puzzles, currentChallenge } = payload;

        this.puzzles = puzzles || [];
        this.currentChallenge = currentChallenge;
        this.isLoading = false;

        console.log(`[Challenges] Initial state: ${this.puzzles.length} puzzles, active=${!!currentChallenge}`);

        this.notifyUpdateListeners();
    }

    // =====================================
    // Event Listeners
    // =====================================

    /**
     * Subscribe to real-time progress updates
     * @param {Function} callback - Called with progress data
     * @returns {Function} Unsubscribe function
     */
    onProgress(callback) {
        this.progressCallbacks.add(callback);
        return () => this.progressCallbacks.delete(callback);
    }

    /**
     * Subscribe to state updates (puzzle list, challenge start/end)
     * @param {Function} callback - Called when state changes
     * @returns {Function} Unsubscribe function
     */
    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    notifyProgressListeners(progress) {
        for (const callback of this.progressCallbacks) {
            try {
                callback(progress);
            } catch (error) {
                console.error('[Challenges] Error in progress callback:', error);
            }
        }
    }

    handleScreenshot(payload) {
        for (const callback of this.screenshotCallbacks) {
            try { callback(payload.dataUrl); } catch (e) { /* */ }
        }
    }

    /**
     * Subscribe to screenshot updates
     */
    onScreenshot(callback) {
        this.screenshotCallbacks.add(callback);
        return () => this.screenshotCallbacks.delete(callback);
    }

    notifyUpdateListeners() {
        const data = {
            puzzles: this.puzzles,
            currentChallenge: this.currentChallenge,
            isLoading: this.isLoading
        };

        for (const callback of this.updateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Challenges] Error in update callback:', error);
            }
        }
    }

    // =====================================
    // Getters
    // =====================================

    getPuzzles() {
        return this.puzzles;
    }

    getCurrentChallenge() {
        return this.currentChallenge;
    }

    getIsLoading() {
        return this.isLoading;
    }

    // =====================================
    // Utility Functions
    // =====================================

    /**
     * Format time as M:SS
     * @param {number} ms - Milliseconds
     * @returns {string} Formatted time
     */
    static formatTime(ms) {
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }

    /**
     * Get difficulty color
     * @param {string} difficulty - Difficulty level
     * @returns {string} CSS color
     */
    static getDifficultyColor(difficulty) {
        const colors = {
            'easy': '#22c55e',
            'medium': '#f59e0b',
            'hard': '#ef4444',
            'cruel': '#a855f7'
        };
        return colors[difficulty] || '#6b7280';
    }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[Challenges] Cleaning up...');
        this.puzzles = [];
        this.currentChallenge = null;
        this.progressCallbacks.clear();
        this.updateCallbacks.clear();
        this.screenshotCallbacks.clear();
        this._pendingRequests.clear();
    }
}
